"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog-client";
import { AP_FACTORS, PERIOD_DATA, PERIOD_OPTIONS } from "./data";
import { DomainsView, HistoryView, TrendsView } from "./views";
import type { PeriodKey, ProductVariant, ViewKey } from "./types";

interface Props {
  view: ViewKey;
  variant: ProductVariant;
  accessPoints: string[];
}

export default function AnalyticsDashboard({ view, variant, accessPoints }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const [period, setPeriod] = useState<PeriodKey>("24h");
  const [apIndex, setApIndex] = useState(0);

  const dataset = PERIOD_DATA[period];
  const factor = AP_FACTORS[apIndex];

  function handlePeriodChange(next: PeriodKey) {
    if (next === period) return;
    setPeriod(next);
    trackEvent("analytics_feature_period_changed", { product: variant, period: next });
  }

  function handleApChange(index: number) {
    if (index === apIndex) return;
    setApIndex(index);
    trackEvent("analytics_feature_access_point_changed", {
      product: variant,
      access_point: accessPoints[index],
    });
  }

  return (
    <div className="border-border bg-card overflow-hidden rounded-2xl border">
      {/* Window chrome */}
      <div className="border-border bg-foreground/[0.02] flex items-center gap-2 border-b px-4 py-3">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="bg-foreground/15 size-2.5 rounded-full" />
          <span className="bg-foreground/15 size-2.5 rounded-full" />
          <span className="bg-foreground/15 size-2.5 rounded-full" />
        </span>
        <span className="text-muted-foreground ml-2 truncate font-mono text-[11px]">
          app.proxidize.com — Analytics
        </span>
      </div>

      {/* Filter bar */}
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div
          className="scrollbar-hide flex gap-1.5 overflow-x-auto"
          role="group"
          aria-label="Filter by access point"
        >
          {accessPoints.map((ap, i) => (
            <button
              key={ap}
              type="button"
              onClick={() => handleApChange(i)}
              aria-pressed={apIndex === i}
              className={cn(
                "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors",
                apIndex === i
                  ? "text-foreground border-foreground/25 bg-foreground/10"
                  : "border-border text-muted-foreground hover:text-foreground/80 hover:bg-foreground/[0.04]"
              )}
            >
              {ap}
            </button>
          ))}
        </div>

        <div
          className="border-border flex shrink-0 rounded-lg border p-0.5"
          role="group"
          aria-label="Filter by time period"
        >
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => handlePeriodChange(option.key)}
              aria-pressed={period === option.key}
              className={cn(
                "rounded-md px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors",
                period === option.key
                  ? "text-foreground bg-foreground/10"
                  : "text-muted-foreground hover:text-foreground/80"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div
        id="analytics-dashboard-panel"
        role="tabpanel"
        className="h-[432px] p-4 sm:h-[460px] sm:p-6"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${view}-${period}-${apIndex}`}
            className="h-full"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {view === "trends" && <TrendsView dataset={dataset} factor={factor} />}
            {view === "domains" && <DomainsView dataset={dataset} factor={factor} />}
            {view === "history" && (
              <HistoryView dataset={dataset} accessPoints={accessPoints} apIndex={apIndex} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
